import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatEUR } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartDrawer() {
  const { items, subtotal, count, setQty, removeItem, drawerOpen, closeDrawer } = useCart();
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/shop/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url) throw new Error(data.detail || "checkout failed");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            key="cart-backdrop"
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeDrawer}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          />
          <motion.aside
            key="cart-panel"
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-clan-bg shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-clan-ink/10 px-6 py-5">
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-5 w-5 text-clan-accent" />
                <h2 className="font-serif text-xl text-clan-ink">
                  Your basket
                  <span className="ml-2 font-mono text-xs text-clan-muted">({count})</span>
                </h2>
              </div>
              <button
                data-testid="cart-close-btn"
                onClick={closeDrawer}
                aria-label="Close basket"
                className="grid h-9 w-9 place-items-center rounded-md border border-clan-ink/15 text-clan-ink transition-colors hover:border-clan-accent/60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div data-lenis-prevent className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <div className="grid h-full place-items-center text-center">
                  <div>
                    <ShoppingBag className="mx-auto h-10 w-10 text-clan-muted/40" />
                    <p className="mt-4 font-serif text-lg italic text-clan-muted">Your basket is empty.</p>
                  </div>
                </div>
              ) : (
                <ul className="flex flex-col gap-5">
                  {items.map((i) => (
                    <li key={i.product_id} data-testid={`cart-line-${i.product_id}`} className="flex gap-4">
                      {i.image_url && (
                        <img src={i.image_url} alt={i.name} className="h-20 w-20 shrink-0 rounded-md object-cover" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-base leading-snug text-clan-ink">{i.name}</p>
                        <p className="mt-0.5 font-mono text-xs text-clan-accent">{formatEUR(i.price)}</p>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex items-center rounded-md border border-clan-ink/15">
                            <button
                              data-testid={`cart-dec-${i.product_id}`}
                              onClick={() => setQty(i.product_id, i.quantity - 1)}
                              aria-label="Decrease quantity"
                              className="grid h-7 w-7 place-items-center text-clan-muted hover:text-clan-ink"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-7 text-center font-mono text-sm text-clan-ink">{i.quantity}</span>
                            <button
                              data-testid={`cart-inc-${i.product_id}`}
                              onClick={() => setQty(i.product_id, i.quantity + 1)}
                              aria-label="Increase quantity"
                              className="grid h-7 w-7 place-items-center text-clan-muted hover:text-clan-ink"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <button
                            data-testid={`cart-remove-${i.product_id}`}
                            onClick={() => removeItem(i.product_id)}
                            aria-label="Remove item"
                            className="text-clan-muted transition-colors hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <p className="shrink-0 font-mono text-sm text-clan-ink">{formatEUR(i.price * i.quantity)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-clan-ink/10 px-6 py-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-clan-muted">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-serif text-2xl text-clan-ink">{formatEUR(subtotal)}</span>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-clan-muted">
                  Shipping & any tax calculated at checkout
                </p>
                <button
                  data-testid="cart-checkout-btn"
                  onClick={checkout}
                  disabled={busy}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-clan-primary px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  {busy ? "Redirecting to secure checkout…" : "Checkout securely"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
